import { useState, useEffect } from 'react';
import { Search, Bell, Link2, RefreshCw, Settings, Plus, CheckCircle2, AlertCircle } from 'lucide-react';
import { collection, query, where, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { Connector, ConnectorType } from '../../types/content';
import { Badge, EmptyState } from '../shared';

interface AvailableConnector {
  type: ConnectorType;
  name: string;
  description: string;
  icon: string;
  comingSoon?: boolean;
}

const AVAILABLE_CONNECTORS: AvailableConnector[] = [
  { type: 'shopify', name: 'Shopify', description: 'Sync products and collections', icon: '🛍️' },
  { type: 'sftp', name: 'SFTP', description: 'Import files via SFTP', icon: '📁' },
  { type: 'sharepoint', name: 'SharePoint', description: 'Microsoft SharePoint sites', icon: '📊', comingSoon: true },
  { type: 'hubspot', name: 'HubSpot', description: 'CRM and content', icon: '🟠', comingSoon: true },
  { type: 'contentful', name: 'Contentful', description: 'Headless CMS', icon: '🌐', comingSoon: true },
  { type: 'wordpress', name: 'WordPress', description: 'Posts and pages', icon: '📝', comingSoon: true },
  { type: 'webflow', name: 'Webflow', description: 'CMS collections', icon: '💨', comingSoon: true },
  { type: 'sanity', name: 'Sanity', description: 'Structured content', icon: '🎨', comingSoon: true },
  { type: 'strapi', name: 'Strapi', description: 'Headless CMS', icon: '🚀', comingSoon: true },
];

export function Connectors() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [syncing, setSyncing] = useState<string | null>(null);

  useEffect(() => {
    loadConnectors();
  }, [user]);

  const loadConnectors = async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, 'connectors'),
        where('userId', '==', user.uid)
      );
      const snapshot = await getDocs(q);
      setConnectors(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }) as Connector));
    } catch (error) {
      console.error('Error loading connectors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (connectorId: string) => {
    setSyncing(connectorId);
    try {
      await updateDoc(doc(db, 'connectors', connectorId), {
        status: 'syncing',
        updatedAt: Timestamp.now()
      });

      // Simulate sync (in production, this would trigger a Cloud Function)
      setTimeout(async () => {
        await updateDoc(doc(db, 'connectors', connectorId), {
          status: 'active',
          lastSync: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
        setSyncing(null);
        loadConnectors();
      }, 2000);
    } catch (error) {
      console.error('Error syncing connector:', error);
      setSyncing(null);
    }
  };

  const handleConnect = (type: ConnectorType) => {
    if (type === 'shopify' || type === 'sftp') {
      // Navigate to SourceOfTruth to configure
      window.location.href = '/source';
    } else {
      alert('This connector is coming soon!');
    }
  };

  const activeConnectors = connectors.filter(c => c.status === 'active' || c.status === 'syncing');
  const inactiveConnectors = connectors.filter(c => c.status === 'inactive' || c.status === 'error');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t2-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Content</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Connectors</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search connectors..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t2-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold text-t2-primary uppercase tracking-wide mb-1">Kodiac Content</p>
          <h1 className="text-3xl font-semibold text-gray-900">Connectors</h1>
          <p className="text-sm text-gray-600 mt-1">Manage content source connections and sync settings</p>
        </div>

        {/* Active Connectors */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900">Active Connectors</h2>
            <button
              onClick={() => loadConnectors()}
              className="text-sm text-t2-primary hover:text-t2-dark flex items-center gap-1"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>

          {activeConnectors.length === 0 ? (
            <EmptyState
              icon={Link2}
              title="No active connectors"
              description="Connect your content sources below to start syncing"
            />
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {activeConnectors.map(connector => (
                <div key={connector.id} className="bg-surface-warm rounded-lg border border-gray-200 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">{connector.name}</h3>
                      <Badge
                        variant={
                          connector.status === 'active'
                            ? 'success'
                            : connector.status === 'syncing'
                            ? 'primary'
                            : 'critical'
                        }
                      >
                        {connector.status}
                      </Badge>
                    </div>
                    <button className="p-1 text-gray-400 hover:text-gray-600">
                      <Settings className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-2 text-xs text-gray-600 mb-3">
                    <div className="flex items-center justify-between">
                      <span>Records:</span>
                      <span className="font-medium text-gray-900">{connector.recordCount}</span>
                    </div>
                    {connector.lastSync && (
                      <div className="flex items-center justify-between">
                        <span>Last sync:</span>
                        <span className="font-medium text-gray-900">
                          {connector.lastSync.toDate().toLocaleTimeString()}
                        </span>
                      </div>
                    )}
                    {connector.errorCount > 0 && (
                      <div className="flex items-center gap-1 text-critical-red">
                        <AlertCircle className="w-3 h-3" />
                        <span>{connector.errorCount} errors</span>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleSync(connector.id)}
                    disabled={connector.status === 'syncing' || syncing === connector.id}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-t2-primary text-white rounded-lg hover:bg-t2-dark disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    <RefreshCw className={`w-4 h-4 ${syncing === connector.id ? 'animate-spin' : ''}`} />
                    {syncing === connector.id ? 'Syncing...' : 'Sync Now'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Available to Connect */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Available to Connect</h2>
          <div className="grid grid-cols-3 gap-4">
            {AVAILABLE_CONNECTORS.map(connector => {
              const isConnected = connectors.some(c => c.type === connector.type);
              return (
                <div
                  key={connector.type}
                  className={`rounded-lg border-2 ${
                    isConnected ? 'border-success-green bg-green-50' : 'border-gray-200 bg-white'
                  } p-4`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="text-3xl">{connector.icon}</div>
                    {isConnected && <CheckCircle2 className="w-5 h-5 text-success-green" />}
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">{connector.name}</h3>
                  <p className="text-xs text-gray-600 mb-3">{connector.description}</p>
                  {isConnected ? (
                    <div className="text-xs text-success-green font-medium">Connected</div>
                  ) : connector.comingSoon ? (
                    <Badge variant="warning">Coming Soon</Badge>
                  ) : (
                    <button
                      onClick={() => handleConnect(connector.type)}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-t2-primary text-t2-primary rounded-lg hover:bg-t2-primary hover:text-white text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Connect
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Inactive/Error Connectors */}
        {inactiveConnectors.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mt-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Inactive Connectors</h2>
            <div className="space-y-2">
              {inactiveConnectors.map(connector => (
                <div
                  key={connector.id}
                  className="flex items-center justify-between p-3 bg-surface-warm rounded-lg border border-gray-200"
                >
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{connector.name}</h3>
                    <Badge variant="critical">{connector.status}</Badge>
                  </div>
                  <button className="text-sm text-t2-primary hover:text-t2-dark">Reactivate</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
